import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const formatEur = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

const PLANS = {
  monthly: { starter: 39, pro: 99, business: 250 },
  annual: { starter: 32, pro: 82, business: 207 },
};

function getPlan(users) {
  if (users <= 1) return "starter";
  if (users <= 10) return "pro";
  return "business";
}

const PLAN_LABELS = {
  starter: "Starter",
  pro: "Pro",
  business: "Business",
};

const Slider = ({ label, value, min, max, step, onChange, format }) => (
  <div className="mb-6">
    <div className="flex justify-between items-center mb-2">
      <span className="text-sm font-medium text-gray-300">{label}</span>
      <span className="text-base font-bold text-blue-400">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ accentColor: "#3b82f6" }}
      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
    />
    <div className="flex justify-between text-xs text-gray-500 mt-1">
      <span>{format(min)}</span>
      <span>{format(max)}</span>
    </div>
  </div>
);

const MetricCard = ({ label, value, sub, highlight }) => (
  <div
    className="rounded-xl p-4 text-center"
    style={{ background: highlight ? "#2563eb" : "#1f2937" }}
  >
    <div
      className="text-xl font-extrabold"
      style={{ color: highlight ? "#fff" : "#60a5fa" }}
    >
      {value}
    </div>
    <div
      className="text-xs font-semibold mt-1"
      style={{ color: highlight ? "#bfdbfe" : "#d1d5db" }}
    >
      {label}
    </div>
    {sub && (
      <div
        className="text-xs mt-1"
        style={{ color: highlight ? "#dbeafe" : "#6b7280" }}
      >
        {sub}
      </div>
    )}
  </div>
);

const ROICalculatorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(55);
  const [hoursOnEmail, setHoursOnEmail] = useState(2.5);
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);

  const recommendedPlan = getPlan(users);
  const pricePerMonth = PLANS[billing][recommendedPlan];
  const totalMonthly = users * pricePerMonth;
  const totalAnnual = totalMonthly * 12;

  const workingDays = 220;

  // Cap recovered time at 65% of current email time (realistic upper bound)
  const MAX_RECOVERY_RATIO = 0.65;
  // Floor: user keeps at least 35% of original time on emails
  const MIN_RESIDUAL_RATIO = 0.35;

  const timeSavedPerDay = Math.min(hoursOnEmail * MAX_RECOVERY_RATIO, 2.5);
  const hoursAfter = Math.max(hoursOnEmail - timeSavedPerDay, hoursOnEmail * MIN_RESIDUAL_RATIO);
  const effectiveTimeSaved = hoursOnEmail - hoursAfter;

  const hoursSavedPerYear = users * effectiveTimeSaved * workingDays;
  const moneySavedPerYear = hoursSavedPerYear * hourlyRate;
  const netROI = moneySavedPerYear - totalAnnual;
  const roiMultiple =
    totalAnnual > 0 ? (moneySavedPerYear / totalAnnual).toFixed(1) : "0";
  const paybackMonths =
    moneySavedPerYear > 0
      ? Math.ceil((totalAnnual / moneySavedPerYear) * 12)
      : 0;
  const percentRecovered = Math.min(
    Math.round((effectiveTimeSaved / hoursOnEmail) * 100),
    65
  );

  const handleStartTrial = async () => {
    if (!user) {
      navigate("/register");
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/billing/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan_id: recommendedPlan, origin_url: originUrl })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la création du paiement");
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  return (
    <div
      style={{ background: "#030712", minHeight: "100vh" }}
      className="text-white"
    >
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">
              Voir les tarifs →
            </Link>
          </div>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>
      </div>

      {/* Calculator */}
      <div className="flex items-center justify-center p-4 pt-6">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-widest"
              style={{ background: "#2563eb" }}
            >
              Calculateur ROI
            </div>
            <h1 className="text-3xl font-extrabold mb-2" style={{ fontFamily: 'Outfit' }}>
              Combien ALIA vous fait{" "}
              <span style={{ color: "#60a5fa" }}>économiser ?</span>
            </h1>
            <p className="text-sm" style={{ color: "#9ca3af" }}>
              Ajustez les paramètres selon votre contexte. Le résultat est
              instantané.
            </p>
          </div>

          <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
            {/* LEFT */}
            <div
              className="rounded-2xl p-6"
              style={{ background: "#111827", border: "1px solid #1f2937" }}
            >
              <h2
                className="text-xs font-bold uppercase tracking-widest mb-5"
                style={{ color: "#6b7280" }}
              >
                Votre contexte
              </h2>

              {/* Billing toggle */}
              <div
                className="flex rounded-xl p-1 mb-6"
                style={{ background: "#1f2937" }}
              >
                {["monthly", "annual"].map((b) => (
                  <button
                    key={b}
                    onClick={() => setBilling(b)}
                    className="flex-1 py-2 text-xs font-semibold rounded-lg transition-all"
                    style={{
                      background: billing === b ? "#2563eb" : "transparent",
                      color: billing === b ? "#fff" : "#9ca3af",
                    }}
                  >
                    {b === "monthly" ? "Mensuel" : "Annuel −17%"}
                  </button>
                ))}
              </div>

              <Slider
                label="Nombre d'utilisateurs"
                value={users}
                min={1}
                max={50}
                step={1}
                onChange={setUsers}
                format={(v) => `${v} utilisateur${v > 1 ? "s" : ""}`}
              />
              <Slider
                label="Coût horaire moyen (chargé)"
                value={hourlyRate}
                min={20}
                max={150}
                step={5}
                onChange={setHourlyRate}
                format={(v) => `${v}€/h`}
              />
              <Slider
                label="Temps actuel sur les emails/jour"
                value={hoursOnEmail}
                min={0.5}
                max={5}
                step={0.5}
                onChange={setHoursOnEmail}
                format={(v) => `${v}h/jour`}
              />

              {/* Cost box - shows real recommended plan price */}
              <div
                className="mt-2 rounded-xl p-4 text-center"
                style={{ background: "#1f2937", border: "1px solid #374151" }}
                data-testid="roi-plan-cost-box"
              >
                <div className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: "#9ca3af" }}>
                  Plan recommandé · {PLAN_LABELS[recommendedPlan]}
                </div>
                <div className="text-2xl sm:text-3xl font-extrabold text-white" data-testid="roi-plan-price">
                  {pricePerMonth}€
                  <span
                    className="text-sm font-normal"
                    style={{ color: "#9ca3af" }}
                  >
                    {recommendedPlan === "business" ? "/mois (illimité)" : "/utilisateur/mois"}
                  </span>
                </div>
                <div className="text-base sm:text-lg mt-2" style={{ color: "#60a5fa" }} data-testid="roi-plan-total">
                  {recommendedPlan === "business"
                    ? `Forfait illimité ${formatEur(pricePerMonth)}/mois`
                    : `soit ${formatEur(totalMonthly)}/mois pour ${users} utilisateur${users > 1 ? "s" : ""}`}
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col gap-4">
              <div
                className="rounded-2xl p-6"
                style={{ background: "#111827", border: "1px solid #1f2937" }}
              >
                <h2
                  className="text-xs font-bold uppercase tracking-widest mb-4"
                  style={{ color: "#6b7280" }}
                >
                  Votre gain estimé
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard
                    label="Économies annuelles"
                    value={formatEur(moneySavedPerYear)}
                    sub={`${Math.round(hoursSavedPerYear).toLocaleString(
                      "fr-FR"
                    )}h récupérées`}
                  />
                  <MetricCard
                    label="ROI net annuel"
                    value={formatEur(netROI)}
                    sub="après déduction ALIA"
                    highlight
                  />
                  <MetricCard
                    label="Multiplicateur ROI"
                    value={`×${roiMultiple}`}
                    sub="retour sur invest."
                  />
                  <MetricCard
                    label="Payback"
                    value={`${paybackMonths} mois`}
                    sub="amortissement"
                  />
                </div>
              </div>

              {/* Progress bar */}
              <div
                className="rounded-2xl p-5"
                style={{ background: "#111827", border: "1px solid #1f2937" }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs" style={{ color: "#9ca3af" }}>
                    Temps email récupéré avec ALIA
                  </span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: "#60a5fa" }}
                  >
                    {percentRecovered}%
                  </span>
                </div>
                <div
                  className="w-full rounded-full"
                  style={{ background: "#374151", height: "10px" }}
                >
                  <div
                    className="rounded-full transition-all duration-500"
                    style={{
                      width: `${percentRecovered}%`,
                      height: "10px",
                      background: "#3b82f6",
                    }}
                  />
                </div>
                <div
                  className="flex justify-between text-xs mt-2"
                  style={{ color: "#6b7280" }}
                >
                  <span data-testid="roi-time-before">Avant : {hoursOnEmail.toFixed(1)}h/jour</span>
                  <span data-testid="roi-time-after">
                    Après : {hoursAfter.toFixed(1)}h/jour
                  </span>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={handleStartTrial}
                disabled={loading}
                className="w-full font-bold py-4 rounded-xl text-sm transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: "#2563eb", color: "#fff" }}
                data-testid="roi-start-trial-btn"
              >
                {loading ? "Redirection..." : "Démarrer mon essai gratuit 14 jours →"}
              </button>
              <p className="text-center text-xs" style={{ color: "#6b7280" }}>
                Sans engagement · Onboarding inclus · Carte ou IBAN
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculatorPage;
